import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import logger from 'ws-scripts/modules/logger';

import {
	setPaymentComplete,
	setTradeInComplete,
	setCreditStatusComplete,
	setTestDriveComplete,
	setProtectionPlansComplete
} from '../features/steps/stepsSlice';
import useEventBus from './useEventBus';
import getLsWalletId from '../helpers/getLsWalletId';

/*
 * Track Steps Progress from SDP events
 */
const useStepsListener = () => {
	const dispatch = useDispatch();
	const [eventBus, isReady] = useEventBus();

	useEffect(() => {
		if (isReady) {
			const setProtectionPlansStatus = () => {
				if (getLsWalletId()) {
					window.setTimeout(() => {
						eventBus.emit('wallet:getDealDetails:request', {
							walletId: getLsWalletId()
						});
					}, 2000);
				}
			};
			const handleSdpOnTrackCompleted = (sdpEventObject = {}) => {
				const { name } = sdpEventObject;

				logger.info(
					`WS-TRACK-MY-OFFER -> UPDATE FROM SDP (uds:sdpOnTrack:completed): ${JSON.stringify(
						sdpEventObject,
						null,
						2
					)}`
				);

				switch (name) {
					case 'DR_TRADEIN_ESTIMATE_RECEIVED': // trade in event name
					case 'DR_TRADEIN_ACTION': // apply trade in value event name (button in DS)
						dispatch(setTradeInComplete(true));
						break;
					case 'DR_CREDIT_APP_SUCCESS_DISPLAYED': // when credit application is submitted
						dispatch(setCreditStatusComplete(true));
						break;
					case 'DR_TEST_DRIVE_REQUESTED': // when test drive request is submitted
						dispatch(setTestDriveComplete(true));
						break;
					case 'DR_DXG_ID_VERSION':
						setProtectionPlansStatus();
						break;
					case 'DR_MENU_PRODUCT_ADDED':
					case 'DR_MENU_PRODUCT_REMOVED':
						dispatch(setProtectionPlansComplete(true));
						break;
					default:
						// TODO: should this be tracked using sdp on load event to avoid updating for every sdp event
						dispatch(setPaymentComplete(true));
				}
			};

			const handleDealDetails = (data) => {
				const firstVehicleProtectionProducts =
					data[0]?.vehicleProtection?.products;
				if (firstVehicleProtectionProducts?.length > 0) {
					if (firstVehicleProtectionProducts[0]?.price) {
						dispatch(setProtectionPlansComplete(true));
					}
				} else {
					dispatch(setProtectionPlansComplete(false));
				}
			};

			eventBus.on('uds:sdpOnTrack:completed', handleSdpOnTrackCompleted);
			eventBus.on('wallet:getDealDetails:completed', handleDealDetails);

			return () => {
				eventBus.off(
					'uds:sdpOnTrack:completed',
					handleSdpOnTrackCompleted
				);
				eventBus.off(
					'wallet:getDealDetails:completed',
					handleDealDetails
				);
			};
		}
		return () => {};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [eventBus, isReady]);

	useEffect(() => {
		if (isReady) {
			const handleWalletInit = ({ paymentInfo }) => {
				logger.info(
					`WS-TRACK-MY-OFFER -> Wallet Init (wallet:ensurePaymentsInfo:completed): ${JSON.stringify(
						paymentInfo,
						null,
						2
					)}`
				);
				dispatch(setTradeInComplete(!!paymentInfo?.tradeIn?.value));
			};

			const handleWalletUpdates = (walletUpdate) => {
				const { updatedPaymentsInfo = {} } = walletUpdate;
				logger.info(
					`WS-TRACK-MY-OFFER -> update from wallet (wallet:updatePaymentsInfo:completed): ${JSON.stringify(
						updatedPaymentsInfo,
						null,
						2
					)}`
				);
				dispatch(
					setTradeInComplete(!!updatedPaymentsInfo?.tradeIn?.value)
				);
			};

			// TODO: These events are fired multiple times on init and updates, not sure if we can re-visit and do something here
			eventBus.on(
				'wallet:ensurePaymentsInfo:completed',
				handleWalletInit
			);
			eventBus.on(
				'wallet:updatePaymentsInfo:completed',
				handleWalletUpdates
			);

			return () => {
				eventBus.off(
					'wallet:ensurePaymentsInfo:completed',
					handleWalletInit
				);
				eventBus.off(
					'wallet:updatePaymentsInfo:completed',
					handleWalletUpdates
				);
			};
		}
		return () => {};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [eventBus, isReady]);
};

export default useStepsListener;
